import Chip, { type ChipProps } from '@mui/material/Chip';
import { AlertOctagon, AlertTriangle, CheckCircle2, Circle, Info, Loader2 } from 'lucide-react';
import type { VorgangStatus } from '@autopilot/contracts';
import { red, status as st } from '../theme/theme';
import { VORGANG_STATUS_LABEL, type Tone } from '../lib/labels';

const TONE: Record<Tone, { fg: string; bg: string; border: string }> = {
  success: st.success,
  warning: st.warning,
  error: st.error,
  info: st.info,
  neutral: st.neutral,
  brand: { fg: red[700], bg: red[50], border: red[200] },
};

const ICON: Record<Tone, typeof Info> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertOctagon,
  info: Info,
  neutral: Circle,
  brand: Circle,
};

export interface ToneBadgeProps extends Omit<ChipProps, 'label' | 'color' | 'icon'> {
  tone: Tone;
  label: string;
  /** Icon immer bei Fehler/Warnung (Farbe allein signalisiert keinen Fehler). */
  showIcon?: boolean;
  spinning?: boolean;
  /** Langen Text umbrechen statt abschneiden (enge Tabellenspalten). */
  wrap?: boolean;
}

export function ToneBadge({ tone, label, showIcon, spinning, wrap, size = 'small', sx, ...rest }: ToneBadgeProps) {
  const s = TONE[tone] ?? TONE.neutral;
  const Icon = spinning ? Loader2 : ICON[tone];
  const withIcon = showIcon ?? (tone === 'error' || tone === 'warning' || tone === 'success' || spinning);
  return (
    <Chip
      size={size}
      variant="outlined"
      label={label}
      icon={withIcon ? <Icon size={14} aria-hidden style={{ color: s.fg }} className={spinning ? 'spin' : undefined} /> : undefined}
      sx={{
        color: s.fg,
        backgroundColor: s.bg,
        borderColor: s.border,
        fontWeight: 600,
        maxWidth: '100%',
        ...(wrap ? { height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.3 } } : {}),
        ...sx,
      }}
      {...rest}
    />
  );
}

const STATUS_TONE: Record<VorgangStatus, Tone> = {
  NEU: 'info',
  KLASSIFIZIERT: 'info',
  IN_BEARBEITUNG: 'info',
  WARTET_EXTERN: 'neutral',
  WARTET_MELDER: 'neutral',
  GELOEST: 'success',
  GESCHLOSSEN: 'neutral',
};

/** Vorgangsstatus als Chip; unbekannte Werte grau mit Rohnamen statt Absturz. */
export function StatusBadge({ status, ...rest }: { status: VorgangStatus; wrap?: boolean } & Omit<ChipProps, 'label' | 'color'>) {
  return <ToneBadge tone={STATUS_TONE[status] ?? 'neutral'} label={VORGANG_STATUS_LABEL[status] ?? status} showIcon={false} {...rest} />;
}
